import { Modal } from 'ant-design-vue'
import { sendFaceFail } from '@/api/evaInfo'
import type { FaceExamineStatus, InfoStepItem } from '@/api/activity/type'
import type { ActifaceCompareRecordItem, CompareState } from '@/api/evaInfo/type'
import { router } from '@/router/index'
import { resolveCompareState } from '@/views/evaInfo/faceCompareState'
import { store } from './index'

export const useEvaInfoStore = defineStore(
  'APP_EVA_INFO',
  () => {
    const { t } = useI18n()

    const actifaceCompareRecord = ref<Record<string, Partial<ActifaceCompareRecordItem>>>({})
    const faceUseNumberByActi = ref<Recordable>({})
    const finishedComparePaperIds = ref<Recordable<number[]>>({})
    const notPassActiIds = ref<number[]>([])

    const faceConfidence = ref(0)
    const similarity = 60
    const currentStep = ref(0)
    const steps = ref<InfoStepItem[]>([])
    const faceNumber = ref(3)
    const faceCompareLinkId = ref<number[]>([])
    const isFaceExamine = ref(false)
    const faceExamineStatus = ref<FaceExamineStatus | null>(null)
    const activityRoute = ref<any>({})
    const isFacePass = ref(false)
    const collectorPaperIds = ref<number[]>([])
    const faceComparePaperIds = ref<number[]>([])
    const actiId = ref<number>(0)
    const facePhotoType = ref<'IdCard' | 'IdPhoto'>('IdPhoto')

    const faceQueueNum = ref(0)
    const isShowFaceQueueDialog = ref(false)
    const hasCompared = ref(unref(faceConfidence) > 0)

    const backEvaluationPage = () => {
      router.replace({ name: 'evaluation' })
    }

    const nextStep = () => {
      if (actifaceCompareRecord.value[unref(actiId)]?.state === 'failNotPass') {
        backEvaluationPage()
        return
      }

      if (currentStep.value < unref(steps).length - 1) {
        currentStep.value++
      } else {
        backEvaluationPage()
      }
    }

    const resetCompareRecord = () => {
      Object.keys(actifaceCompareRecord.value).forEach((currentActiId) => {
        actifaceCompareRecord.value[currentActiId].fromPage = 'login'
      })
    }

    const updateCurrentCompareRecord = ({
      state,
      faceUseNumber,
      fromPage
    }: Partial<ActifaceCompareRecordItem>) => {
      actifaceCompareRecord.value[unref(actiId)] ??= {
        fromPage: 'login',
        paperIds: [],
        isFacePass: unref(isFacePass),
        faceUseNumber: 0,
        faceNumber: 3,
        state: 'tips'
      }

      const currentCompareRecord = actifaceCompareRecord.value[unref(actiId)]
      currentCompareRecord.faceNumber = unref(faceNumber)
      currentCompareRecord.paperIds = unref(faceComparePaperIds)
      currentCompareRecord.isFacePass = unref(isFacePass)

      if (state) {
        currentCompareRecord.state = state
      }

      if (faceUseNumber) {
        const currentUseNumber = currentCompareRecord.faceUseNumber || 0
        currentCompareRecord.faceUseNumber = currentUseNumber + 1
      }

      if (fromPage) {
        currentCompareRecord.fromPage = fromPage
      }

      return currentCompareRecord
    }

    const localizedSteps = computed(() => {
      return unref(steps).map(({ title, ...rest }) => {
        return {
          ...rest,
          title: t(title)
        }
      })
    })

    const isComparePassed = computed(() => {
      return unref(faceConfidence) > similarity && unref(hasCompared)
    })

    const remainingCompareCount = computed(() => {
      const usedCount = unref(actifaceCompareRecord)[unref(actiId)]?.faceUseNumber || 0
      return unref(faceNumber) - usedCount
    })

    const currentCompareState = computed<CompareState>(() => {
      return resolveCompareState({
        remainingCompareCount: unref(remainingCompareCount),
        isFacePass: unref(isFacePass),
        isPass: unref(isComparePassed),
        hasCompared: unref(hasCompared)
      })
    })

    const goBack = () => {
      router.push(unref(activityRoute))
    }

    async function setExamine() {
      await nextTick()

      if (unref(currentCompareState) === 'failNotPass') {
        sendFaceFail({
          actiLinkId: unref(faceCompareLinkId)[0],
          faceCompareStatus: 'fail'
        }).then(() => {
          let secondsToGo = 3
          const buildReviewMessage = (seconds: number) =>
            `人脸识别未通过，请等待管理员审核，${seconds}S后返回`

          const modal = Modal.info({
            title: t('common.systemPrompt'),
            content: buildReviewMessage(secondsToGo),
            okText: '立即返回',
            onOk: goBack
          })

          const interval = setInterval(() => {
            secondsToGo -= 1
            modal.update({
              content: buildReviewMessage(secondsToGo)
            })
          }, 1000)

          setTimeout(() => {
            clearInterval(interval)
            modal.destroy()
            goBack()
          }, secondsToGo * 1000)
        })
      } else if (unref(currentCompareState) === 'success') {
        sendFaceFail({
          actiLinkId: unref(faceCompareLinkId)[0],
          faceCompareStatus: 'success'
        })
      }
    }

    const afterCompare = () => {
      hasCompared.value = true
      updateCurrentCompareRecord({ faceUseNumber: 1, fromPage: 'other' })

      if (unref(isFaceExamine)) {
        setExamine()
      }
    }

    // 兼容历史调用，外部仍可继续使用旧字段和旧方法名。
    const realStep = localizedSteps
    const isPass = isComparePassed
    const compareState = currentCompareState
    const isCampare = hasCompared
    const setPaperFinished = updateCurrentCompareRecord
    const resetComapareRecord = resetCompareRecord

    return {
      steps,
      localizedSteps,
      realStep,
      currentStep,
      collectorPaperIds,
      faceComparePaperIds,
      actiId,
      faceQueueNum,
      faceNumber,
      faceUseNumberByActi,
      finishedComparePaperIds,
      isFacePass,
      faceConfidence,
      notPassActiIds,
      actifaceCompareRecord,
      faceCompareLinkId,
      isFaceExamine,
      faceExamineStatus,
      activityRoute,
      resetCompareRecord,
      resetComapareRecord,
      nextStep,
      backEvaluationPage,
      updateCurrentCompareRecord,
      setPaperFinished,
      isShowFaceQueueDialog,
      hasCompared,
      isCampare,
      afterCompare,
      isComparePassed,
      isPass,
      remainingCompareCount,
      currentCompareState,
      compareState,
      similarity,
      facePhotoType
    }
  },
  {
    persist: {
      paths: [
        'steps',
        'realStep',
        'currentStep',
        'collectorPaperIds',
        'faceComparePaperIds',
        'faceCompareLinkId',
        'actiId',
        'faceNumber',
        'faceUseNumberByActi',
        'finishedComparePaperIds',
        'actifaceCompareRecord',
        'notPassActiIds',
        'faceConfidence',
        'activityRoute',
        'faceExamineStatus',
        'isFaceExamine',
        'similarity',
        'isFacePass',
        'isCampare',
        'isPass',
        'compareState',
        'facePhotoType'
      ]
    }
  }
)

export function useEvaInfoStoreWithOut() {
  return useEvaInfoStore(store)
}
